import { Autocomplete, AutocompleteChangeDetails, AutocompleteChangeReason, Chip, TextField } from "@mui/material";
import { isString } from "lodash-es";
import React from "react";
import { FaCircleCheck } from 'react-icons/fa6';
import { MdDoNotDisturb } from "react-icons/md";
import { CreateTaggingParams, useCreateTagging, useDestroyTagging, useUpdateTagging } from "~/api/tagging_queries";
import { useFetchTags } from "~/api/tags_queries";
import { notify } from "~/components/Notifications";
import { Tag } from "~/shared/types/tag";
import { SerializedModelWithTaggings, Tagging } from "~/shared/types/tagging";

const getSelectedOptions = (taggings: Tagging[], tags: Tag[] | undefined) => {
  if (!tags) return []
  const taggingIds = taggings.map((tagging) => tagging.tag_id);

  return tags.filter(tag => taggingIds.includes(tag.id));
};

export default function TagsInput({
  entity,
  tagCategoryId,
  inputLabel,
  afterChange,
}: {
  entity: SerializedModelWithTaggings;
  tagCategoryId: number;
  inputLabel: string;
  afterChange?: () => void;
}) {

  const entityType = entity.type
  const entityId = entity.id;
  const taggings = entity.taggings
  const fetchTagsQuery = useFetchTags({ tagCategoryId })
  const { data: tags } = fetchTagsQuery
  const createTaggingQuery = useCreateTagging()
  const destroyTaggingQuery = useDestroyTagging()
  const updateTaggingQuery = useUpdateTagging()
  const selectedOptions = getSelectedOptions(taggings, tags);

  const disabled = !fetchTagsQuery.isSuccess ||
    fetchTagsQuery.isLoading ||
    createTaggingQuery.isPending

  const handleCreateTagging = async (params: CreateTaggingParams) => {
    try {
      await createTaggingQuery.mutateAsync(params)
      await fetchTagsQuery.refetch()
      afterChange?.();
    } catch (error) {
      notify('Error creating tagging', 'error')
    }
  }

  const handleDestroyTagging = async (tagid: number) => {
    try {
      const taggingId = taggings.find(tagging => tagging.tag_id === tagid)?.id
      if (!taggingId) return
      await destroyTaggingQuery.mutateAsync(taggingId)
      await fetchTagsQuery.refetch()
      afterChange?.();
    } catch (error) {
      console.log(error);
      notify('Error destroying tagging', 'error')
    }
  }

  const handleToggleTaggingDeactivated = async (taggingId: number, deactivated: boolean) => {
    try {
      await updateTaggingQuery.mutateAsync({ id: taggingId, deactivated })
      afterChange?.();
      const action = deactivated ? 'Deactivated' : 'Reactivated';
      notify(`${action} tagging`, 'success')
    } catch (error) {
      notify('Error updating tagging', 'error')
    }
  }

  const handleChange = (
    event: React.SyntheticEvent,
    value: (string | Tag)[],
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<Tag> // contains the option that was added or removed
  ) => {

    let name: string

    switch (reason) {
      case "selectOption":
        // selected options should provide a Tag object
        if (!details?.option.name) break
        name = details?.option.name

        handleCreateTagging({
          tagging: {
            tag_id: isString(details?.option) ? 0 : details?.option.id,
            entity_type: entityType,
            entity_id: entityId,
          },
        })
        break;
      case "createOption":
        // createOption should provide a string value
        if (!isString(details?.option)) break
        name = details?.option

        handleCreateTagging({
          tagging: {
            tag_id: undefined,
            entity_type: entityType,
            entity_id: entityId,
          },
          tag: {
            name,
            tag_category_id: tagCategoryId,
          }
        })
        break;
      case "removeOption":
        if (!details?.option.id) break
        handleDestroyTagging(details?.option.id)
        break;
      case "clear":
        // no-op for now
        break
      default:
        break;
    }
    console.log({ event, value, reason, details });
  };


  return (
    <Autocomplete
      multiple
      id="tags-standard"
      disableClearable
      freeSolo // allow values that aren't in the list of options
      options={tags || []}
      value={selectedOptions}
      getOptionLabel={(option) => isString(option) ? option : option.name} // with freesolo option comes in as string
      disabled={disabled}
      onChange={handleChange}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          if (isString(option)) {
            return <Chip label={option} {...getTagProps({ index })} />
          }

          const tagging = taggings.find((tagging) => tagging.tag_id === option.id)
          const deactivated = tagging?.deactivated

          return (
            <Chip
              icon={deactivated ? <MdDoNotDisturb size={20} /> : <FaCircleCheck size={20} />}
              label={option.name}
              color={deactivated ? 'warning' : 'success'}
              variant={deactivated ? 'outlined' : 'filled'}
              onClick={() => tagging && handleToggleTaggingDeactivated(tagging.id, !tagging.deactivated)}
              {...getTagProps({ index })}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant="filled"
          label={inputLabel}
          sx={{ '& .MuiFilledInput-root': { pt: 3 } }}
        />
      )}
    />
  );

}
